import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "./icon";

const navLinks = [
  { name: "Services", href: "#services" },
  { name: "Why us", href: "#features" },
  { name: "About", href: "#about" },
  { name: "Work", href: "#portfolio" },
  { name: "Process", href: "#process" },
  { name: "Team", href: "#team" },
];

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);

      const scrollable = document.body.scrollHeight - window.innerHeight;
      setProgress(scrollable > 0 ? Math.min(y / scrollable, 1) : 0);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll while the mobile sheet is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  /* Over the dark hero the bar is transparent with light text; once the page
     scrolls — or the mobile sheet opens — it becomes a frosted white surface
     with navy text so the bar and the sheet read as one element. */
  const solid = scrolled || isOpen;
  const onDark = !solid;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ease-smooth ${
        solid
          ? "border-b border-line/70 bg-white/85 py-3 shadow-sm backdrop-blur-xl"
          : "border-b border-transparent bg-transparent py-5"
      }`}
    >
      <nav className="container flex items-center justify-between gap-6">
        <a
          href="#page-top"
          className="flex shrink-0 items-center rounded-lg"
          aria-label="Mercuri — back to top"
        >
          <img
            src="img/mercuri_logo.png"
            alt="Mercuri"
            className={`w-auto transition-all duration-500 ease-smooth ${
              solid ? "h-9" : "h-10"
            }`}
          />
        </a>

        <div className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className={`rounded-lg px-3.5 py-2 text-sm font-medium transition-colors duration-300 ${
                onDark
                  ? "text-white/80 hover:bg-white/10 hover:text-white"
                  : "text-ink-soft hover:bg-navy-50 hover:text-navy-800"
              }`}
            >
              {link.name}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            className={`hidden text-sm md:inline-flex ${
              onDark ? "btn-ghost-light" : "btn-primary"
            } px-5 py-2.5`}
          >
            Get in touch
            <Icon name="arrowRight" className="h-4 w-4" />
          </a>

          <button
            type="button"
            onClick={() => setIsOpen((open) => !open)}
            aria-expanded={isOpen}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border transition-colors duration-300 lg:hidden ${
              onDark
                ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
                : "border-line bg-white text-navy-800 hover:bg-navy-50"
            }`}
          >
            <Icon name={isOpen ? "close" : "menu"} className="h-5 w-5" />
          </button>
        </div>
      </nav>

      {/* Reading-progress hairline */}
      <div
        className={`absolute inset-x-0 bottom-0 h-0.5 origin-left bg-gradient-to-r from-crimson-600 to-crimson-400 transition-opacity duration-500 ${
          scrolled ? "opacity-100" : "opacity-0"
        }`}
        style={{ transform: `scaleX(${progress})` }}
      />

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 top-full border-t border-line bg-white shadow-lift lg:hidden"
          >
            <div className="container flex flex-col py-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between border-b border-line py-4 text-base font-medium text-navy-900 transition-colors last:border-0 hover:text-crimson-600"
                >
                  {link.name}
                  <Icon name="arrowUpRight" className="h-4 w-4 text-ink-faint" />
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setIsOpen(false)}
                className="btn-primary mt-4 w-full"
              >
                Get in touch
                <Icon name="arrowRight" className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
